import { trackEvent } from "src/Amplitude";
import {
  Box,
  Collapse,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { createStyles, makeStyles } from "@mui/styles";
import React from "react";
import TableAddNote from "src/icons/TableAddNote";
import ElipsisIcon from "src/icons/transactions/ElipsisIcon";
import api from "src/store/features/api";
import TableAddNoteFilled from "../../icons/TableAddNoteFilled";
import CountryOrCryptoFlag from "../common/CountryOrCryptoFlag";
import DoubleCell from "./DoubleCell";
import { createDataDesktop } from "./helperfns";
import NoteDialog from "./NoteDialog";
import useCopyElementWidth from "./transactionHooks/useCopyElementWidth";
import useSelectedStyles from "./transactionHooks/useSelectedStyles";
import { useLocation } from "react-router-dom";
import { addTransactionNote, useTransactionSlice } from "src/store/features/transactions.slice";
import { useAppDispatch } from "src/store/store";

const RowStyles = makeStyles((theme: Theme) =>
  createStyles({
    TableRow: {
      "& > *": { borderBottom: "none", padding: "14px" },

      cursor: "pointer",
      "&:hover": {
        background: theme.palette.mode === "light" ? "#e5e9eb" : "#5e5e5e",
      },
    },
    CollapsedTableHead: {
      "& > *": {
        borderBottom: "none",
        fontWeight: 600,
        color: "#8d9da7",
        fontSize: "14px",
      },
    },
    CollapsedTableBody: {
      "& > *": { padding: "16px", borderBottom: "none" },
    },
    CollapsedTotalRow: {
      backgroundColor: theme.palette.primary.contrastText,
      height: "54px",
      "& > *": {
        borderBottom: "none",
        fontWeight: 600,
        color: theme.palette.common.black,
        fontSize: "14px",
      },
    },
    timeColumn: {
      fontWeight: 600,
    },
    currencyBackground: {
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "32px",
      height: "32px",
      backgroundColor: theme.palette.primary.contrastText,
    },
    collaspedTableRow: {
      borderBottom: "0.1px solid #d1d4d6",
    },
    collaspedBox: {
      marginTop: 1,
      marginBottom: 0,
    },
    rowTypeTypography: {
      fontWeight: 600,
      color: theme.palette.common.black,
      fontSize: "14px",
    },
    tableCellSide: {
      borderBottom: "none",
      fontWeight: 600,
      color: "#8d9da7",
      fontSize: "14px",
    },
  }),
);

interface RowProps {
  row: ReturnType<typeof createDataDesktop>;
}

const Row = (props: RowProps) => {
  const { row } = props;
  const { notes } = useTransactionSlice();
  const dispatch = useAppDispatch();
  const classes = RowStyles();
  const [open, setOpen] = React.useState(false);
  const [openAddNote, setOpenAddNote] = React.useState(false);
  const [addNewNote, setAddNewNote] = React.useState("");
  const hasNote = addNewNote?.length > 0;
  const [showNotification, setShowNotification] = React.useState(false);

  React.useEffect(() => {
    notes.forEach((note) => {
      if (note.id === row.id) {
        setAddNewNote(note.note); // set the note in the row
      }
    });
  }, [notes]);

  // This is for the recent transactions component in the Portfolio page.
  // when a user clicks the "View" button, the user is redirected to this page and opens the ct row corresponding to the ct id in the url.
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const ct = queryParams.get("ct");
  React.useEffect(() => {
    if (ct && ct === row.id) {
      setOpen(true);
    }
  }, [ct]);

  const [postNote] = api.useAddNoteToCtMutation();

  const handleClose = () => {
    setOpenAddNote(false);
  };

  const handleOnConfirmClose = () => {
    setShowNotification(true);
    setTimeout(() => {
      setOpenAddNote(false);
      setTimeout(() => {
        setShowNotification(false);
      }, 500);
    }, 1000);
  };

  const handleNoteConfirmation = async () => {
    trackEvent("Select_Transaction_Page_Notes_Add_Notes");
    handleOnConfirmClose();

    const maxNoteLength = 500;
    let newNote;

    if (addNewNote.length > maxNoteLength) {
      newNote = row.note ?? "";
    } else {
      newNote = addNewNote;
    }

    let note = {
      ctNoteDto: {
        ctId: row.id,
        note: newNote,
      },
    };
    try {
      await postNote(note);
      dispatch(addTransactionNote({ id: row.id, note: addNewNote }));
    } catch (e) {
      console.warn("handleNoteConfirmation", e);
    }
  };

  const { onToggle: handleSelectedStyles, selected: selectedStyles } = useSelectedStyles();

  const { typeRef, dateRef, currencyRef, amountRef, statusRef, width } = useCopyElementWidth();

  const DisplayCurrencyIcons = () => {
    return (
      <Grid container alignItems="center" spacing={2}>
        {row.currency.map((currency, index) => {
          if (index > 4) {
            return <div key={`${currency.code}-${index}`}></div>;
          }

          if (index === 0) {
            return (
              <Grid item key={`${currency.code}-${index}`}>
                <CountryOrCryptoFlag name={currency.img} size={24} />
              </Grid>
            );
          }

          if (index === 4) {
            return (
              <Grid item key={`${currency.code}-${index}`}>
                <div className={classes.currencyBackground}>
                  <ElipsisIcon />
                </div>
              </Grid>
            );
          }

          return (
            <Grid item key={`${currency.code}-${index}`}>
              <div className={classes.currencyBackground}>
                <CountryOrCryptoFlag name={currency.img} size={16} />
              </div>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <React.Fragment>
      <TableRow
        data-testid="transactions-collapsible-row"
        onClick={() => {
          setOpen((prev) => !prev);
          handleSelectedStyles(!open);
        }}
        className={classes.TableRow}
        sx={{ backgroundColor: selectedStyles.backgroundColor }}>
        <TableCell ref={typeRef}>
          <DoubleCell
            left={row.icon}
            right={<Typography className={classes.rowTypeTypography}>{row.type}</Typography>}
          />
        </TableCell>
        <TableCell ref={currencyRef}>
          <DisplayCurrencyIcons />
        </TableCell>
        <TableCell ref={dateRef}>
          <Grid container direction="column">
            <Grid item className={classes.timeColumn}>
              {row.date}
            </Grid>
            <Grid item>{row.time}</Grid>
          </Grid>
        </TableCell>

        <TableCell ref={amountRef} translate="no">
          {row.amount}
        </TableCell>
        <TableCell ref={statusRef}>
          <DoubleCell left={row.status[0]} right={row.status[1]} />
        </TableCell>
        <TableCell>
          <div
            onClick={(e: any) => {
              e.preventDefault();
              e.stopPropagation();
              setOpenAddNote(true);
              trackEvent("Select_Transaction_Page_Notes");
            }}>
            {hasNote ? <TableAddNoteFilled /> : <TableAddNote />}
          </div>
        </TableCell>
      </TableRow>

      {/* COLLAPSIBLE TABLE */}
      <TableRow className={classes.collaspedTableRow}>
        <TableCell style={{ padding: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box className={classes.collaspedBox}>
              <Table size="small" aria-label="transaction-details">
                <TableHead>
                  <TableRow className={classes.CollapsedTableHead}>
                    <TableCell colSpan={1} />
                    <TableCell>Currency</TableCell>
                    <TableCell>Units</TableCell>
                    <TableCell>Units Price</TableCell>
                    <TableCell>Fee</TableCell>
                    <TableCell>Value</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {row.lines.map((line, index) => {
                    return (
                      <TableRow
                        key={`${line.side}-${index}`}
                        className={classes.CollapsedTableBody}>
                        <TableCell
                          align="center"
                          className={classes.tableCellSide}
                          sx={{ width: width.type }}>
                          {line.side}
                        </TableCell>

                        <TableCell sx={{ width: width.currency / 2 }}>
                          <DoubleCell
                            left={<CountryOrCryptoFlag name={line.base[0]} size={24} />}
                            right={line.base[1]}
                          />
                        </TableCell>
                        <TableCell sx={{ width: width.currency / 2 }} translate="no">
                          {line.units}
                        </TableCell>
                        <TableCell sx={{ width: width.date }} translate="no">
                          {line.price}
                        </TableCell>
                        <TableCell sx={{ width: width.amount }} translate="no">
                          {line.fee}
                        </TableCell>
                        <TableCell sx={{ width: width.status }} translate="no">
                          {line.value}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className={classes.CollapsedTotalRow}>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell translate="no">Total: {row.amount}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <NoteDialog
        open={openAddNote}
        onClose={handleClose}
        addNewNote={addNewNote}
        setAddNewNote={setAddNewNote}
        showNotification={showNotification}
        onConfirm={handleNoteConfirmation}
      />
    </React.Fragment>
  );
};

export default Row;
